import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { VideoNodeView } from "./video-node-view";

export interface VideoOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    video: {
      /**
       * Insert a video node
       */
      insertVideo: (options: {
        src: string;
        poster?: string;
        caption?: string;
        width?: number | string;
        height?: number | string;
        align?: "left" | "center" | "right";
      }) => ReturnType;
    };
  }
}

/**
 * Video extension for TipTap editor
 * Supports embedding videos from URLs (including DocVideo videoUrl)
 */
export const VideoNode = Node.create<VideoOptions>({
  name: "video",

  group: "block",

  atom: true,

  draggable: true,

  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => {
          if (!attributes.src) {
            return {};
          }
          return {
            src: attributes.src,
          };
        },
      },
      poster: {
        default: null,
        parseHTML: (element) => element.getAttribute("poster"),
        renderHTML: (attributes) => {
          if (!attributes.poster) {
            return {};
          }
          return {
            poster: attributes.poster,
          };
        },
      },
      caption: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-caption"),
        renderHTML: (attributes) => {
          if (!attributes.caption) {
            return {};
          }
          return {
            "data-caption": attributes.caption,
          };
        },
      },
      width: {
        default: "100%",
        parseHTML: (element) => element.getAttribute("width") || "100%",
        renderHTML: (attributes) => {
          return {
            width: attributes.width || "100%",
          };
        },
      },
      height: {
        default: "auto",
        parseHTML: (element) => element.getAttribute("height") || "auto",
        renderHTML: (attributes) => {
          return {
            height: attributes.height || "auto",
          };
        },
      },
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") || "center",
        renderHTML: (attributes) => {
          return {
            "data-align": attributes.align || "center",
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="video"]',
      },
      {
        tag: 'video[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "video",
      }),
      [
        "video",
        {
          src: HTMLAttributes.src,
          poster: HTMLAttributes.poster,
          controls: true,
          width: HTMLAttributes.width,
          height: HTMLAttributes.height,
          style: `width: ${HTMLAttributes.width}; height: ${HTMLAttributes.height};`,
        },
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoNodeView);
  },

  addCommands() {
    return {
      insertVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});
